import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Center, Spinner, Text, VStack } from "@chakra-ui/react";
import { useAuth } from "@context/AuthContext";

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 *
 * Guards routes by checking the user's authentication.
 * If the user is not authenticated, redirects to /login while saving the requested URL.
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps): JSX.Element => {
    const { user, loading } = useAuth();
    const location = useLocation();

    // Show a spinner while authentication is being checked
    if (loading) {
        return (
            <Center flex="1" bg="gray.50">
                <VStack spacing={4}>
                    <Spinner size="xl" color="principal.blue" thickness="4px" />
                    <Text fontSize="lg" color="gray.600">
                        Vérification de l'authentification...
                    </Text>
                </VStack>
            </Center>
        );
    }

    // If the user is not authenticated, redirect to /login
    // The requested URL is saved in location.state.from
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // If the user is authenticated, render the protected content
    return <>{children}</>;
};

export default ProtectedRoute;
